import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send, CheckCircle2, XCircle, Mail, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Label,
  ScrollArea,
  Input,
  useAuth,
  toast,
} from '@aaas/shared';
import { useEmailTemplateListQuery, usePreviewEmailMutation } from '../hooks/useEmailQueries';
import { useEmailSignatureListQuery } from '../hooks/useEmailSignatureQueries';
import emailService from '../services/email.service';
import type { EmailRecipient, EmailTemplate, BulkSendResult } from '../types';
import { TiptapEmailEditor } from './TiptapEmailEditor';

interface SendEmailDialogProps {
  open: boolean;
  onClose: () => void;
  cvIds: string[];
  jobId?: string;
  /** If set, enriches template variables with event fields (title, type, times, location). */
  calendarEventId?: string;
  /** Pre-populated CC list. In the calendar-event flow this is PIC + attendees. */
  initialCcEmails?: EmailRecipient[];
  /** Called after a successful send so the caller can e.g. invalidate queries. */
  onSuccess?: () => void;
}

type Step = 'select-template' | 'edit' | 'sending' | 'result';

/** Extract all {{variable_name}} placeholders from a string */
function extractPlaceholders(text: string): string[] {
  const regex = /\{\{([^}]+)\}\}/g;
  const found = new Set<string>();
  let match;
  while ((match = regex.exec(text)) !== null) {
    found.add(match[1].trim());
  }
  return Array.from(found);
}

/** Replace {{variable}} occurrences with provided values in HTML string */
function applyVariables(html: string, vars: Record<string, string>): string {
  let result = html;
  for (const [key, value] of Object.entries(vars)) {
    result = result.split(`{{${key}}}`).join(value);
  }
  return result;
}

export function SendEmailDialog({
  open,
  onClose,
  cvIds,
  jobId,
  calendarEventId,
  initialCcEmails,
  onSuccess,
}: SendEmailDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace, idToken } = useAuth();
  const [step, setStep] = useState<Step>('select-template');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkSendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Edit step state
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBodyHtml, setEditedBodyHtml] = useState('');
  const [missingVars, setMissingVars] = useState<string[]>([]);
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const [selectedSignatureId, setSelectedSignatureId] = useState<string>('');

  const isBulk = cvIds.length > 1;
  const firstCvId = cvIds[0];

  // Fetch templates
  const { data: templateResponse } = useEmailTemplateListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: open,
    size: 100,
  });
  const templates = templateResponse?.data?.items || [];

  // Fetch user's signatures (scoped to current user + workspace by backend)
  const { data: signatureResponse } = useEmailSignatureListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: open,
    size: 100,
  });
  const signatures = signatureResponse?.data?.items || [];

  // Preview mutation
  const previewMutation = usePreviewEmailMutation();

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep('select-template');
      setSelectedTemplateId(null);
      setIsSending(false);
      setBulkResult(null);
      setError(null);
      setEditedSubject('');
      setEditedBodyHtml('');
      setMissingVars([]);
      setVarValues({});
      setSelectedSignatureId('');
      previewMutation.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Called when user clicks "Next" on template selection — fetches preview and moves to edit step */
  const handleLoadPreview = useCallback(async () => {
    if (!selectedTemplateId || !firstCvId) return;
    setError(null);
    try {
      const result = await previewMutation.mutateAsync({
        template_id: selectedTemplateId,
        cv_id: firstCvId,
        job_id: jobId,
        calendar_event_id: calendarEventId,
      });
      const preview = result.data;

      // Detect unresolved placeholders in subject + body
      const subjectPlaceholders = extractPlaceholders(preview.subject);
      const bodyPlaceholders = extractPlaceholders(preview.body_html);
      const allMissing = Array.from(new Set([...subjectPlaceholders, ...bodyPlaceholders]));

      setEditedSubject(preview.subject);
      setEditedBodyHtml(preview.body_html);
      setMissingVars(allMissing);
      setVarValues(Object.fromEntries(allMissing.map((v) => [v, ''])));
      setStep('edit');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('email.send.loadPreviewFailed'));
    }
  }, [selectedTemplateId, firstCvId, jobId, calendarEventId, previewMutation, t]);

  /** Live preview of body with user-filled variables applied */
  const resolvedBodyHtml = useMemo(() => {
    return applyVariables(editedBodyHtml, varValues);
  }, [editedBodyHtml, varValues]);

  const resolvedSubject = useMemo(() => {
    return applyVariables(editedSubject, varValues);
  }, [editedSubject, varValues]);

  /** Remaining unresolved placeholders after user has typed values */
  const stillMissing = useMemo(() => {
    return missingVars.filter((v) => !varValues[v]?.trim());
  }, [missingVars, varValues]);

  const handleSend = useCallback(async () => {
    if (!user || !currentWorkspace || !selectedTemplateId) return;

    setIsSending(true);
    setStep('sending');
    setError(null);

    // Build the final body with user edits + variable substitution
    const finalBodyHtml = resolvedBodyHtml;
    const finalSubject = resolvedSubject;
    const customVars = Object.fromEntries(
      Object.entries(varValues).filter(([, v]) => v.trim())
    );

    try {
      const token = await user.getIdToken();

      if (isBulk) {
        const response = await emailService.bulkSendEmail(currentWorkspace.id, token, {
          template_id: selectedTemplateId,
          cv_ids: cvIds,
          job_id: jobId,
          custom_variables: Object.keys(customVars).length > 0 ? customVars : undefined,
          body_html: finalBodyHtml !== undefined ? finalBodyHtml : undefined,
          subject: finalSubject !== undefined ? finalSubject : undefined,
          signature_id: selectedSignatureId || undefined,
        });
        setBulkResult(response.data);
        setStep('result');
        if (response.data.failed === 0) {
          toast.success(t('email.send.successCount', { count: response.data.succeeded }));
        } else {
          toast.error(t('email.send.failedCount', { count: response.data.failed }));
        }
      } else {
        await emailService.sendEmail(currentWorkspace.id, token, {
          template_id: selectedTemplateId,
          cv_id: firstCvId,
          job_id: jobId,
          custom_variables: Object.keys(customVars).length > 0 ? customVars : undefined,
          body_html: finalBodyHtml !== undefined ? finalBodyHtml : undefined,
          subject: finalSubject !== undefined ? finalSubject : undefined,
          signature_id: selectedSignatureId || undefined,
          calendar_event_id: calendarEventId,
          cc_emails:
            initialCcEmails && initialCcEmails.length > 0 ? initialCcEmails : undefined,
        });
        setStep('result');
        toast.success(t('email.send.successToast'));
        onSuccess?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('email.send.sendFailed'));
      setStep('edit');
    } finally {
      setIsSending(false);
    }
  }, [
    user,
    currentWorkspace,
    selectedTemplateId,
    cvIds,
    jobId,
    isBulk,
    firstCvId,
    resolvedBodyHtml,
    resolvedSubject,
    varValues,
    selectedSignatureId,
    calendarEventId,
    initialCcEmails,
    onSuccess,
    t,
  ]);

  const handleClose = () => {
    if (!isSending) {
      onClose();
    }
  };

  const preview = previewMutation.data?.data;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {isBulk ? t('email.send.titleBulk', { count: cvIds.length }) : t('email.send.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Select Template */}
        {step === 'select-template' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('email.send.selectTemplateLabel')}</Label>
              {templates.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {t('email.send.noTemplates')}
                </p>
              ) : (
                <ScrollArea className="h-60 border rounded-md">
                  <div className="p-2 space-y-1">
                    {templates.map((tpl: EmailTemplate) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(tpl.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${
                          selectedTemplateId === tpl.id
                            ? 'bg-orange-50 border border-orange-200'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <p className="text-sm font-medium">{tpl.name}</p>
                        <p className="text-xs text-gray-500 truncate">{tpl.subject}</p>
                        {tpl.is_system && (
                          <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mt-1">
                            {t('email.send.systemBadge')}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                {t('email.send.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleLoadPreview}
                disabled={!selectedTemplateId || previewMutation.isPending}
              >
                {previewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                {t('email.send.nextEdit')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 2: Edit */}
        {step === 'edit' && preview && (
          <div className="space-y-4">
            {/* Recipient info */}
            <div className="space-y-1 px-3 py-2 bg-gray-50 rounded-md text-sm">
              <div className="flex items-center gap-4">
                <span className="text-gray-500 shrink-0">{t('email.send.to')}</span>
                <span className="font-medium">
                  {preview.candidate_name} &lt;{preview.to_email}&gt;
                </span>
                {isBulk && cvIds.length > 1 && (
                  <span className="ml-auto text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                    {t('email.send.moreCandidates', { count: cvIds.length - 1 })}
                  </span>
                )}
              </div>
              {initialCcEmails && initialCcEmails.length > 0 && (
                <div className="flex items-start gap-4">
                  <span className="text-gray-500 shrink-0">{t('email.send.cc')}</span>
                  <span className="text-gray-700">
                    {initialCcEmails
                      .map((r) => (r.name ? `${r.name} <${r.email}>` : r.email))
                      .join(', ')}
                  </span>
                </div>
              )}
            </div>

            {/* Bulk notice */}
            {isBulk && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700">
                  {t('email.send.bulkNotice')}
                </p>
              </div>
            )}

            {/* Missing variables panel */}
            {missingVars.length > 0 && (
              <div className="border border-orange-200 bg-orange-50 rounded-md p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
                  <p className="text-sm font-medium text-orange-800">
                    {stillMissing.length > 0
                      ? t('email.send.missingCount', { count: stillMissing.length })
                      : t('email.send.missingFilled')}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {missingVars.map((varName) => (
                    <div key={varName} className="space-y-1">
                      <Label className="text-xs text-orange-700 font-mono">{`{{${varName}}}`}</Label>
                      <Input
                        type="text"
                        placeholder={t('email.send.varPlaceholder', { name: varName })}
                        value={varValues[varName] ?? ''}
                        onChange={(e) =>
                          setVarValues((prev) => ({ ...prev, [varName]: e.target.value }))
                        }
                        className={`h-8 text-sm ${
                          !varValues[varName]?.trim()
                            ? 'border-orange-300 focus-visible:ring-orange-400'
                            : 'border-green-300 focus-visible:ring-green-400'
                        }`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subject field */}
            <div className="space-y-1.5">
              <Label htmlFor="email-subject" className="text-sm">
                {t('email.send.subjectLabel')}
              </Label>
              <Input
                id="email-subject"
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                placeholder={t('email.send.subjectPlaceholder')}
                className="text-sm"
              />
              {/* Show resolved subject preview if there are vars */}
              {missingVars.length > 0 && resolvedSubject !== editedSubject && (
                <p className="text-xs text-gray-500">
                  <span className="text-gray-800 italic">{t('email.send.subjectPreview', { value: resolvedSubject })}</span>
                </p>
              )}
            </div>

            {/* Body editor */}
            <div className="space-y-1.5">
              <Label className="text-sm">{t('email.send.bodyLabel')}</Label>
              <TiptapEmailEditor
                value={editedBodyHtml}
                onChange={setEditedBodyHtml}
                placeholder={t('email.send.bodyPlaceholder')}
              />
            </div>

            {/* Signature selector */}
            <div className="space-y-1.5">
              <Label htmlFor="email-signature" className="text-sm">
                {t('email.send.signatureLabel')}
              </Label>
              <select
                id="email-signature"
                value={selectedSignatureId}
                onChange={(e) => setSelectedSignatureId(e.target.value)}
                disabled={isSending}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">{t('email.send.signatureNone')}</option>
                {signatures.map((sig) => (
                  <option key={sig.id} value={sig.id}>
                    {sig.name}
                    {sig.is_default ? t('email.send.signatureDefault') : ''}
                  </option>
                ))}
              </select>
              {signatures.length === 0 && (
                <p className="text-xs text-gray-500">
                  {t('email.send.noSignatures')}
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('select-template')}
              >
                {t('email.send.back')}
              </Button>
              <Button
                type="button"
                onClick={handleSend}
                disabled={isSending || stillMissing.length > 0}
                title={
                  stillMissing.length > 0
                    ? t('email.send.fillFirst', { vars: stillMissing.map((v) => `{{${v}}}`).join(', ') })
                    : undefined
                }
              >
                <Send className="h-4 w-4 mr-2" />
                {isBulk ? t('email.send.sendBulk', { count: cvIds.length }) : t('email.send.send')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Sending */}
        {step === 'sending' && (
          <div className="flex flex-col items-center py-8 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            <p className="text-sm text-gray-600">{t('email.send.sending')}</p>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 'result' && (
          <div className="space-y-4">
            {isBulk && bulkResult ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4 justify-center py-2">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{bulkResult.succeeded}</p>
                    <p className="text-xs text-gray-500">{t('email.send.succeeded')}</p>
                  </div>
                  {bulkResult.failed > 0 && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{bulkResult.failed}</p>
                      <p className="text-xs text-gray-500">{t('email.send.failed')}</p>
                    </div>
                  )}
                </div>
                {bulkResult.failed > 0 && (
                  <ScrollArea className="h-40 border rounded-md">
                    <div className="p-2 space-y-1">
                      {bulkResult.results
                        .filter((r) => !r.success)
                        .map((r) => (
                          <div
                            key={r.cv_id}
                            className="flex items-start gap-2 p-2 text-sm bg-red-50 rounded"
                          >
                            <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="font-medium">{t('email.send.cvLabel', { id: r.cv_id })}</p>
                              <p className="text-xs text-red-600">{r.error}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 gap-2">
                <CheckCircle2 className="h-10 w-10 text-green-500" />
                <p className="text-sm font-medium">{t('email.send.successSingle')}</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" onClick={handleClose}>
                {t('email.send.close')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
